/**
 * 参考片口播逐句分析(2026-10-10「追爆出口原话复用」§2 / 冻结契约 C1)。
 *
 * 一次分析,两个出口共用:数字人逐句对照编辑区 + 新闻舱「借热度写 / 仿它的说法」选句。
 * 放 entities 层:消费方在 features/digital-human 与 pages/reporter,features 互不 import(ADR-0013)。
 *
 * ★契约字段名一个都不许改(三仓并行,以 docs/2026-10-10-追爆出口-原话复用设计.md §7 为准)。
 */

export type BlueprintLineRole =
  | "hook"
  | "resonance"
  | "selling"
  | "price"
  | "proof"
  | "cta"
  | "other";

export type RefFactKind = "number" | "brand" | "product" | "story" | "promise" | "cert";

export interface RefFact {
  /** 必是 text 的子串(ai 侧已丢掉不是子串的 span;前端再验一次)。 */
  span: string;
  kind: RefFactKind;
}

export interface BlueprintLine {
  idx: number;
  /** 原话,逐字不改。 */
  text: string;
  role: BlueprintLineRole;
  refFacts: RefFact[];
}

export interface BlueprintLinesResult {
  lines: BlueprintLine[];
  cached: boolean;
}

const ROLES = new Set<string>(["hook", "resonance", "selling", "price", "proof", "cta", "other"]);
const KINDS = new Set<string>(["number", "brand", "product", "story", "promise", "cert"]);

/** 角色中文标签(逐句区左侧小标签;新闻舱选句也用)。 */
export const LINE_ROLE_LABEL: Record<BlueprintLineRole, string> = {
  hook: "钩子",
  resonance: "共鸣",
  selling: "卖点",
  price: "价格",
  proof: "背书",
  cta: "收尾",
  other: "正文",
};

/** refFacts 种类的人话(黄标提示里列出「含参考片的数字 / 品牌…」)。 */
export const REF_FACT_KIND_LABEL: Record<RefFactKind, string> = {
  number: "数字",
  brand: "品牌",
  product: "产品名",
  story: "故事",
  promise: "承诺",
  cert: "资质",
};

/**
 * 后端回包逐行验型:形状不对的行丢掉、未知 role 归 other、不是子串的 span 丢掉,
 * 最后按 idx 升序。不信任上游 —— 一条脏数据不该让整个编辑区白屏。
 */
export function parseBlueprintLines(raw: unknown): BlueprintLine[] {
  if (!Array.isArray(raw)) return [];
  const out: BlueprintLine[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    if (typeof o.idx !== "number" || !Number.isInteger(o.idx)) continue;
    if (typeof o.text !== "string" || !o.text.trim()) continue;
    const text = o.text;
    const role = (
      typeof o.role === "string" && ROLES.has(o.role) ? o.role : "other"
    ) as BlueprintLineRole;
    const refFacts: RefFact[] = [];
    if (Array.isArray(o.refFacts)) {
      for (const f of o.refFacts) {
        if (!f || typeof f !== "object") continue;
        const ff = f as Record<string, unknown>;
        if (typeof ff.span !== "string" || !ff.span || !text.includes(ff.span)) continue;
        if (typeof ff.kind !== "string" || !KINDS.has(ff.kind)) continue;
        refFacts.push({ span: ff.span, kind: ff.kind as RefFactKind });
      }
    }
    out.push({ idx: o.idx, text, role, refFacts });
  }
  return out.sort((a, b) => a.idx - b.idx);
}
